/**
 * UserSearchSelect — reusable PAPI user search with Cloudscape Autosuggest
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import Autosuggest, { AutosuggestProps } from '@cloudscape-design/components/autosuggest';
import { usersApi, UserSearchResult } from '../api/users';
import { badgePhotoUrl } from '../lib/externalUrls';

export interface UserSearchSelectProps {
  /** Called when a user is selected from the results */
  onSelect: (user: UserSearchResult) => void;
  /** Called when the input is cleared */
  onClear?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Clear the input after selection */
  clearOnSelect?: boolean;
  /** Only show managers in search results */
  managersOnly?: boolean;
}

export const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
  onSelect,
  onClear,
  placeholder = 'Search by name or alias...',
  disabled = false,
  clearOnSelect = true,
  managersOnly = false,
}) => {
  const [value, setValue] = useState('');
  const [options, setOptions] = useState<AutosuggestProps.Option[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const resultsRef = useRef<UserSearchResult[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const search = useCallback(async (query: string) => {
    const currentRequestId = ++requestIdRef.current;
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      resultsRef.current = [];
      return;
    }

    setLoading(true);
    try {
      const results = await usersApi.searchUsers(query, managersOnly);
      if (requestIdRef.current !== currentRequestId) return;
      resultsRef.current = results;
      setOptions(
        results.map((user) => ({
          value: user.login,
          label: user.name,
          description: `${user.login} — ${user.title}`,
          iconUrl: badgePhotoUrl(user.login),
          iconAlt: user.name,
        })),
      );
    } catch {
      if (requestIdRef.current !== currentRequestId) return;
      setOptions([]);
      resultsRef.current = [];
    } finally {
      if (requestIdRef.current === currentRequestId) setLoading(false);
    }
  }, [managersOnly]);

  const handleChange: AutosuggestProps['onChange'] = ({ detail }) => {
    setValue(detail.value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!detail.value) {
      setOptions([]);
      resultsRef.current = [];
      if (onClear) onClear();
      return;
    }
    debounceRef.current = setTimeout(() => search(detail.value), 300);
  };

  const handleSelect: AutosuggestProps['onSelect'] = ({ detail }) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const selected = resultsRef.current.find((u) => u.login === detail.value);
    if (selected) {
      onSelect(selected);
      if (clearOnSelect) {
        setValue('');
        setOptions([]);
        resultsRef.current = [];
      } else {
        setValue(selected.name);
      }
    }
  };

  return (
    <div className="user-search-container">
    <Autosuggest
      value={value}
      onChange={handleChange}
      onSelect={handleSelect}
      options={options}
      filteringType="manual"
      statusType={loading ? 'loading' : 'finished'}
      loadingText="Searching..."
      empty="No users found"
      placeholder={placeholder}
      disabled={disabled}
      enteredTextLabel={(v) => `Search for "${v}"`}
    />
    </div>
  );
};

export default UserSearchSelect;
